import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { formatNum, formatSgd, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip } from "recharts";
import {
  Network, Sparkles, ShieldAlert, Leaf, Users, Scale, Gauge, BadgeCheck,
  Globe, AlertTriangle, CheckCircle2, Factory, Building2, Award, MapPin, Flame,
} from "lucide-react";
import { toast } from "sonner";

const RAG = { green: "text-[#00E65B]", amber: "text-[#FFB800]", red: "text-red-500" };
const RAG_BG = { green: "bg-[#00E65B]", amber: "bg-[#FFB800]", red: "bg-red-500" };
const HAZARD_COLOR = { high: "#ef4444", medium: "#FFB800", low: "#00E65B" };
const HAZARD_LABEL = { high: "High hazard", medium: "Medium hazard", low: "Low hazard" };
const RISK_TONE = { low: "text-[#00E65B]", medium: "text-[#FFB800]", high: "text-red-500" };
const PORTAL_TONE = { invited: "warning", active: "lime", submitted: "success", under_review: "warning", overdue: "danger" };
const SBTI_LABEL = { validated: "SBTi Validated", committed: "SBTi Committed", not_committed: "Not committed", unknown: "Unknown" };
const DIM_ICON = { environmental: Leaf, social: Users, governance: Scale, climate: Globe, data_quality: Gauge };
const DIM_LABEL = { environmental: "Environmental", social: "Social", governance: "Governance", climate: "Climate", data_quality: "Data Quality" };

function ImpactTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const s = payload[0].payload;
  return (
    <div className="card-flat p-3 text-xs bg-background/95 border-accent/40 max-w-[220px]">
      <div className="font-semibold text-sm mb-1">{s.name}</div>
      <div className="text-muted-foreground">{s.country} · {s.spend_category}</div>
      <div className="mt-1 font-mono">Spend: {formatSgd(s.annual_spend_sgd)} ({s.spend_pct}%)</div>
      <div className="font-mono">Emissions: {formatNum(s.emissions_tco2e, { decimals: 2 })} tCO₂e
        <span className="text-muted-foreground"> · {s.emissions_basis === "spend_based" ? "spend-based" : "primary"}</span></div>
      <div className="mt-1 flex items-center gap-1" style={{ color: HAZARD_COLOR[s.hazard_band] }}>
        <Flame className="h-3 w-3" />{HAZARD_LABEL[s.hazard_band]} ({s.hazard_score}/5)
      </div>
      {s.top_hazards && s.top_hazards.length > 0 && (
        <div className="text-[10px] text-muted-foreground mt-0.5">{s.top_hazards.map((h) => `${h.name} ${h.level}/5`).join(" · ")}</div>
      )}
    </div>
  );
}

// Schematic geographic map — markers positioned by real lat/lng over the SE-Asia / China
// region; marker size = spend, colour = climate-hazard band. Suppliers sharing a location
// are fanned out slightly so they don't fully overlap.
function GeoMap({ suppliers }) {
  const pts = suppliers.filter((s) => s.lat != null && s.lng != null);
  const W = 440, H = 300, pad = 20;
  const latMin = -9, latMax = 33, lngMin = 97, lngMax = 125;
  const proj = (lat, lng) => ({
    x: pad + ((lng - lngMin) / (lngMax - lngMin)) * (W - 2 * pad),
    y: pad + ((latMax - lat) / (latMax - latMin)) * (H - 2 * pad),
  });
  const maxSpend = Math.max(...pts.map((s) => s.annual_spend_sgd), 1);
  const groups = {};
  pts.forEach((s) => { const k = `${s.lat},${s.lng}`; (groups[k] = groups[k] || []).push(s); });
  const placed = [];
  Object.values(groups).forEach((grp) => {
    grp.forEach((s, idx) => {
      const base = proj(s.lat, s.lng);
      const ang = (idx / Math.max(grp.length, 1)) * Math.PI * 2;
      const r = grp.length > 1 ? 13 : 0;
      placed.push({ ...s, cx: base.x + Math.cos(ang) * r, cy: base.y + Math.sin(ang) * r });
    });
  });
  const eqY = proj(0, lngMin).y;
  const sgY = proj(1.29, lngMin).y;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto rounded-sm" data-testid="si-dollar-impact-map" role="img" aria-label="Supplier hazard exposure map">
      <rect x="0" y="0" width={W} height={H} fill="hsl(var(--surface))" />
      {[0, 1, 2, 3, 4].map((i) => <line key={`v${i}`} x1={pad + (i / 4) * (W - 2 * pad)} y1={pad} x2={pad + (i / 4) * (W - 2 * pad)} y2={H - pad} stroke="hsl(var(--border))" strokeWidth="0.5" strokeDasharray="2 4" />)}
      {[0, 1, 2, 3, 4].map((i) => <line key={`h${i}`} x1={pad} y1={pad + (i / 4) * (H - 2 * pad)} x2={W - pad} y2={pad + (i / 4) * (H - 2 * pad)} stroke="hsl(var(--border))" strokeWidth="0.5" strokeDasharray="2 4" />)}
      <line x1={pad} y1={eqY} x2={W - pad} y2={eqY} stroke="hsl(var(--muted-foreground))" strokeWidth="0.6" strokeDasharray="4 3" opacity="0.5" />
      <text x={pad + 2} y={eqY - 3} fontSize="8" fill="hsl(var(--muted-foreground))">Equator</text>
      <text x={W - pad - 2} y={sgY - 3} fontSize="8" fill="hsl(var(--muted-foreground))" textAnchor="end">Singapore 1.3°N</text>
      {placed.map((s) => {
        const rad = 5 + (s.annual_spend_sgd / maxSpend) * 13;
        return (
          <g key={s.id} data-testid={`si-map-marker-${s.id}`}>
            <circle cx={s.cx} cy={s.cy} r={rad} fill={HAZARD_COLOR[s.hazard_band]} fillOpacity="0.5" stroke={HAZARD_COLOR[s.hazard_band]} strokeWidth="1.2" />
            <circle cx={s.cx} cy={s.cy} r="1.5" fill={HAZARD_COLOR[s.hazard_band]} />
          </g>
        );
      })}
    </svg>
  );
}

function DollarImpactTab({ impact }) {
  if (!impact) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading dollar-impact…</div>;
  if (impact.empty || !impact.suppliers || impact.suppliers.length === 0)
    return <div className="card-flat p-10 text-center text-muted-foreground text-sm" data-testid="si-dollar-impact-empty">No supplier spend to map yet.</div>;
  const { suppliers, totals } = impact;
  return (
    <div className="space-y-4" data-testid="si-dollar-impact">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total spend mapped" value={formatSgd(totals.total_spend)} unit="" icon={Building2} testid="si-di-total-spend" />
        <Stat label="Est. supply-chain emissions" value={formatNum(totals.total_emissions_tco2e, { decimals: 1 })} unit="tCO₂e" icon={Factory} testid="si-di-total-emissions" />
        <Stat label="High-hazard spend" value={formatSgd(totals.high_hazard_spend)} unit="" icon={Flame} tone={totals.high_hazard_count ? "text-red-500" : ""} sub={`${totals.high_hazard_count} supplier${totals.high_hazard_count !== 1 ? "s" : ""}`} testid="si-di-high-hazard" />
        <Stat label="Spend-based estimates" value={totals.spend_based_count} unit="" icon={Gauge} sub="lower-quality EEIO" testid="si-di-spend-based" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-5">
          <div className="label-eyebrow mb-1 flex items-center gap-1.5"><Flame className="h-3.5 w-3.5 text-[#FFB800]" /> Spend × emissions bubble matrix</div>
          <p className="text-xs text-muted-foreground mb-3">Each bubble is a supplier — right = more spend, up = more emissions, size = spend, colour = climate-hazard band. Top-right bubbles are your priority hotspots.</p>
          <div data-testid="si-dollar-impact-chart">
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ top: 8, right: 12, bottom: 28, left: 4 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                <XAxis type="number" dataKey="annual_spend_sgd" name="Annual spend" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <YAxis type="number" dataKey="emissions_tco2e" name="Emissions" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <ZAxis type="number" dataKey="annual_spend_sgd" range={[80, 620]} />
                <Tooltip content={<ImpactTooltip />} cursor={{ strokeDasharray: "3 3" }} />
                {["high", "medium", "low"].map((band) => (
                  <Scatter key={band} name={HAZARD_LABEL[band]} data={suppliers.filter((s) => s.hazard_band === band)} fill={HAZARD_COLOR[band]} fillOpacity={0.65} />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] font-mono">
            {["high", "medium", "low"].map((b) => <span key={b} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: HAZARD_COLOR[b] }} /> {HAZARD_LABEL[b]}</span>)}
          </div>
        </div>

        <div className="card-flat p-5">
          <div className="label-eyebrow mb-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-accent" /> Regional hazard map</div>
          <p className="text-xs text-muted-foreground mb-3">Suppliers placed by location across the SE-Asia / China region — marker size = spend, colour = climate-hazard band.</p>
          <GeoMap suppliers={suppliers} />
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Supplier</TableHead><TableHead>Location</TableHead>
            <TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Emissions (tCO₂e)</TableHead>
            <TableHead>Basis</TableHead><TableHead>Hazard exposure</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {suppliers.map((s) => (
              <TableRow key={s.id} data-testid={`si-dollar-row-${s.id}`}>
                <TableCell className="text-sm font-medium">{s.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{s.location_name}</TableCell>
                <TableCell className="text-right font-mono text-xs">{formatSgd(s.annual_spend_sgd)}<span className="text-muted-foreground"> · {s.spend_pct}%</span></TableCell>
                <TableCell className="text-right font-mono text-xs font-bold">{formatNum(s.emissions_tco2e, { decimals: 2 })}</TableCell>
                <TableCell>{s.emissions_basis === "spend_based" ? <Badge variant="warning" className="text-[9px]">Spend-based</Badge> : <Badge variant="lime" className="text-[9px]">Primary</Badge>}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: HAZARD_COLOR[s.hazard_band] }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: HAZARD_COLOR[s.hazard_band] }} />
                    {HAZARD_LABEL[s.hazard_band]} ({s.hazard_score}/5)
                  </span>
                  {s.top_hazards && s.top_hazards.length > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">{s.top_hazards.map((h) => h.name).join(" · ")}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


function Stat({ label, value, unit, sub, icon: Icon, testid, tone, pkey }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span>{pkey && <ProvenanceTrigger pkey={pkey} className="ml-auto" />}</div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ScoreBar({ dim, value }) {
  const Icon = DIM_ICON[dim];
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="inline-flex items-center gap-1.5"><Icon className="h-3 w-3" />{DIM_LABEL[dim]}</span><span className="font-mono">{value}</span></div>
      <Progress value={value} className="h-1.5" />
    </div>
  );
}

function DetailDialog({ id, open, onClose, weights }) {
  const [s, setS] = useState(null);
  const [ai, setAi] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !id) return;
    setS(null); setAi("");
    api.get(`/supplier-insights/${id}`).then((r) => setS(r.data));
  }, [open, id]);

  const genAi = async () => { setBusy(true); try { const r = await api.post(`/supplier-insights/${id}/ai/risk-recommendation`); setAi(r.data.recommendation); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="si-detail-dialog">
        {!s ? <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div> : (
          <>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2">{s.name}
              <span className={`h-2.5 w-2.5 rounded-full ${RAG_BG[s.rag]}`} /></DialogTitle></DialogHeader>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="outline"><Globe className="h-3 w-3 mr-1" />{s.country}</Badge>
              <Badge variant="outline">{s.spend_category}</Badge>
              <Badge variant={PORTAL_TONE[s.portal_status]}>{s.portal_status.replace(/_/g, " ")}</Badge>
              <Badge variant="outline">{SBTI_LABEL[s.sbti_status]}</Badge>
              {s.cbam_relevant && <Badge variant="warning">CBAM-relevant</Badge>}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">ESG score</div><div className={`font-mono text-2xl font-bold ${RAG[s.rag]}`}>{s.overall_score}</div></div>
              <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Annual spend</div><div className="font-mono text-lg font-bold">{formatSgd(s.annual_spend_sgd)}</div></div>
              <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Supplier S1+S2</div><div className="font-mono text-lg font-bold">{formatNum((s.emissions.scope1_tco2e || 0) + (s.emissions.scope2_tco2e || 0))}<span className="text-xs text-muted-foreground ml-1">t</span></div></div>
              <div className="card-flat p-3"><div className="label-eyebrow text-[10px]">Questionnaire</div><div className="font-mono text-lg font-bold">{s.questionnaire_completion}%</div><Progress value={s.questionnaire_completion} className="h-1 mt-1" /></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="card-flat p-3 space-y-2">
                <div className="label-eyebrow mb-1">Category scores</div>
                {Object.keys(DIM_LABEL).map((dim) => <ScoreBar key={dim} dim={dim} value={s.scores[dim]} />)}
                <div className="text-[10px] text-muted-foreground pt-1">Weighted: {Object.entries(weights || {}).map(([k, v]) => `${DIM_LABEL[k]} ${Math.round(v * 100)}%`).join(" · ")}</div>
              </div>
              <div className="card-flat p-3 space-y-2">
                <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> Risk ratings</div>
                {Object.entries(s.risk_ratings).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span><span className={`font-medium capitalize ${RISK_TONE[v]}`}>{v}</span></div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="card-flat p-3">
                <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Award className="h-3.5 w-3.5" /> Certifications</div>
                {s.certifications.length ? <div className="flex flex-wrap gap-1.5">{s.certifications.map((c) => <Badge key={c} variant="outline" className="text-[10px]"><BadgeCheck className="h-3 w-3 mr-1 text-[#00E65B]" />{c}</Badge>)}</div> : <div className="text-sm text-muted-foreground">No certifications recorded.</div>}
              </div>
              <div className="card-flat p-3">
                <div className="label-eyebrow mb-2">ESG policies</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {Object.entries(s.policies).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1.5">{v ? <CheckCircle2 className="h-3 w-3 text-[#00E65B]" /> : <AlertTriangle className="h-3 w-3 text-muted-foreground" />}<span className="capitalize">{k.replace(/_/g, " ")}</span></div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card-flat p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI risk recommendation</div>
                <Button size="sm" variant="primary" onClick={genAi} disabled={busy} data-testid="si-ai-risk">{busy ? "…" : "Generate"}</Button>
              </div>
              {ai ? <p className="text-sm" data-testid="si-ai-risk-text">{ai}</p> : <p className="text-sm text-muted-foreground">Generate an AI assessment of supplier risk and the recommended engagement action.</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SupplierInsights() {
  const [meta, setMeta] = useState(null);
  const [d, setD] = useState(null);
  const [items, setItems] = useState([]);
  const [impact, setImpact] = useState(null);
  const [active, setActive] = useState(null);
  const [prio, setPrio] = useState("");
  const [pBusy, setPBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/supplier-insights/dashboard"), api.get("/supplier-insights/list")])
      .then(([dd, ll]) => { setD(dd.data); setItems(ll.data); }).catch(() => setD({ empty: true }));
    api.get("/supplier-insights/dollar-impact").then((r) => setImpact(r.data)).catch(() => setImpact({ empty: true }));
  }, []);
  useEffect(() => { api.get("/supplier-insights/meta").then((r) => setMeta(r.data)); load(); }, [load]);

  const genPrio = async () => { setPBusy(true); try { const r = await api.post("/supplier-insights/ai/prioritisation"); setPrio(r.data.recommendation); } catch (e) { toast.error(apiErr(e)); } finally { setPBusy(false); } };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading supplier insights…</div>;
  if (d.empty) return <div className="p-8"><div className="card-flat p-12 text-center"><h2 className="font-display text-2xl font-bold">No supplier profiles yet.</h2><p className="text-muted-foreground mt-2">Supplier ESG profiles are built from ERP, procurement and portal submissions.</p></div></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="supplier-insights-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Supply Chain · Supplier Insights</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><Network className="h-8 w-8 text-accent" /> Supplier Insights</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Supplier ESG profile & analytics database — screening coverage, scoring, risk, SBTi & certifications, Scope 3 data quality and CBAM relevance.</p>
        </div>
      </div>

      {/* top widgets */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Suppliers profiled" value={d.total_suppliers} unit="" icon={Building2} testid="si-stat-total" pkey="si.total" sub={`${formatSgd(d.total_spend)} spend`} />
        <Stat label="ESG screening" value={d.screening.count_pct} unit="%" icon={BadgeCheck} testid="si-stat-screening" pkey="si.screening" sub={`${d.screening.spend_pct}% of spend`} />
        <Stat label="High-risk" value={d.high_risk.count} unit="" icon={ShieldAlert} testid="si-stat-highrisk" pkey="si.highrisk" tone={d.high_risk.count ? "text-red-500" : ""} sub={formatSgd(d.high_risk.spend)} />
        <Stat label="Emissions coverage" value={d.emissions_coverage.spend_pct} unit="%" icon={Factory} testid="si-stat-emissions" pkey="si.emissions_cov" sub={`${formatNum(d.emissions_coverage.supplier_emissions_tco2e)} tCO₂e`} />
        <Stat label="SBTi-aligned spend" value={d.sbti_coverage.spend_pct} unit="%" icon={Globe} testid="si-stat-sbti" pkey="si.sbti" sub={`${d.sbti_coverage.count} suppliers`} />
        <Stat label="Avg ESG score" value={d.avg_esg_score} unit="" icon={Gauge} testid="si-stat-avgscore" pkey="si.avgscore" tone={RAG[d.scope3_data_quality.band]} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="si-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="database" data-testid="si-tab-database">Supplier Database</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="si-tab-scoring">Scoring</TabsTrigger>
          <TabsTrigger value="risk" data-testid="si-tab-risk">Risk & CBAM</TabsTrigger>
          <TabsTrigger value="dollar-impact" data-testid="si-tab-dollar-impact">Dollar-Impact ✦</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-3">Portal submission status</div>
              <div className="space-y-2">
                {Object.entries(d.portal).filter(([, n]) => n > 0).map(([s, n]) => (
                  <div key={s}><div className="flex justify-between text-xs mb-1"><span className="capitalize">{s.replace(/_/g, " ")}</span><span className="font-mono">{n}</span></div>
                    <Progress value={(n / d.total_suppliers) * 100} className="h-1.5" /></div>
                ))}
              </div>
            </div>
            <div className="card-flat p-4 space-y-3">
              <div>
                <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Certifications coverage</div>
                <div className="flex justify-between text-xs mb-1"><span>Suppliers with key certifications</span><span className="font-mono">{d.certifications_coverage.count}/{d.total_suppliers} · {d.certifications_coverage.pct}%</span></div>
                <Progress value={d.certifications_coverage.pct} className="h-1.5" />
              </div>
              <div>
                <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> Scope 3 data quality</div>
                <div className="flex justify-between text-xs mb-1"><span>Avg supplier data-quality score</span><span className={`font-mono ${RAG[d.scope3_data_quality.band]}`}>{d.scope3_data_quality.avg_score}/100</span></div>
                <Progress value={d.scope3_data_quality.avg_score} className="h-1.5" />
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1">
                {["green", "amber", "red"].map((c) => <div key={c} className="text-center border border-border rounded-sm p-2"><div className={`font-mono text-lg font-bold ${RAG[c]}`}>{d.score_distribution[c]}</div><div className="text-[10px] text-muted-foreground capitalize">{c}</div></div>)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Local supplier spend" value={d.local_diverse.local_pct} unit="%" icon={Building2} sub={formatSgd(d.local_diverse.local_spend)} />
            <Stat label="Diverse supplier spend" value={formatNum(d.local_diverse.diverse_spend / 1000, { decimals: 0 })} unit="K S$" icon={Users} />
            <Stat label="CBAM-relevant" value={d.cbam.count} unit="" icon={Factory} tone={d.cbam.count ? "text-[#FFB800]" : ""} />
            <Stat label="Primary-data suppliers" value={d.emissions_coverage.count} unit="" icon={CheckCircle2} />
          </div>
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI supplier prioritisation</div>
              <Button size="sm" variant="primary" onClick={genPrio} disabled={pBusy} data-testid="si-ai-prioritisation">{pBusy ? "Generating…" : "Generate"}</Button>
            </div>
            {prio ? <p className="text-sm leading-relaxed" data-testid="si-prioritisation-text">{prio}</p> : <p className="text-sm text-muted-foreground">Recommend which suppliers to engage first based on spend, ESG score, emissions data gaps and risk.</p>}
          </div>
        </TabsContent>

        {/* Database */}
        <TabsContent value="database" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Supplier</TableHead><TableHead>Country</TableHead><TableHead>Category</TableHead>
                <TableHead className="text-right">Spend</TableHead><TableHead className="text-right">ESG score</TableHead>
                <TableHead>SBTi</TableHead><TableHead>Portal</TableHead><TableHead className="text-right">Questionnaire</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-surface-hover" onClick={() => setActive(s.id)} data-testid={`si-row-${s.id}`}>
                    <TableCell className="text-sm font-medium">{s.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.country}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{s.spend_category}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatSgd(s.annual_spend_sgd)}</TableCell>
                    <TableCell className="text-right"><span className={`inline-flex items-center gap-1.5 font-mono text-sm font-bold ${RAG[s.rag]}`}><span className={`h-2 w-2 rounded-full ${RAG_BG[s.rag]}`} />{s.overall_score}</span></TableCell>
                    <TableCell className="text-[10px] text-muted-foreground">{SBTI_LABEL[s.sbti_status]}</TableCell>
                    <TableCell><Badge variant={PORTAL_TONE[s.portal_status]} className="text-[9px]">{s.portal_status.replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-xs">{s.questionnaire_completion}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Scoring */}
        <TabsContent value="scoring" className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((s) => (
              <button key={s.id} onClick={() => setActive(s.id)} className="card-flat p-4 text-left hover:border-accent/60 transition-colors" data-testid={`si-score-card-${s.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div><div className="text-sm font-medium">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.country} · {formatSgd(s.annual_spend_sgd)}</div></div>
                  <span className={`font-mono text-2xl font-bold ${RAG[s.rag]}`}>{s.overall_score}</span>
                </div>
                <div className="space-y-1.5">{Object.keys(DIM_LABEL).map((dim) => <ScoreBar key={dim} dim={dim} value={s.scores[dim]} />)}</div>
              </button>
            ))}
          </div>
        </TabsContent>

        {/* Risk & CBAM */}
        <TabsContent value="risk" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Supplier</TableHead><TableHead>ESG risk</TableHead><TableHead>Country</TableHead>
                <TableHead>Human rights</TableHead><TableHead>Climate</TableHead><TableHead>Data quality</TableHead>
                <TableHead>CBAM</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer hover:bg-surface-hover" onClick={() => setActive(s.id)} data-testid={`si-risk-row-${s.id}`}>
                    <TableCell className="text-sm font-medium">{s.name}</TableCell>
                    {["esg", "country", "human_rights", "climate", "data_quality"].map((k) => (
                      <TableCell key={k}><span className={`text-xs font-medium capitalize ${RISK_TONE[s.risk_ratings[k]] || "text-muted-foreground"}`}>{s.risk_ratings[k] || "—"}</span></TableCell>
                    ))}
                    <TableCell>{s.cbam_relevant ? <Badge variant="warning" className="text-[9px]">Relevant</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Dollar-Impact */}
        <TabsContent value="dollar-impact" className="mt-4">
          <DollarImpactTab impact={impact} />
        </TabsContent>
      </Tabs>

      <DetailDialog id={active} open={!!active} onClose={() => setActive(null)} weights={meta?.weights} />
    </div>
  );
}
